import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/init-services';
import { renderInputNativeInputChild } from '../../shared/form.util';
import { applyAttribute } from '../../utils/apply-attribute';
import { getRandomId } from '../../utils/get-random-id';

/**
 * Checkboxes let users select one or more items from a list, or turn an item on or off.
 *
 * @slot - Label placed within the element
 * @slot message - `wb-control-error` or `wb-control-hint` to further describe the current state
 */
@Component({
  tag: 'wb-checkbox',
  styleUrl: './checkbox.scss',
  shadow: true,
})
export class CheckboxComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Disables the checkbox */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Marks control as invalid. Automatically set by slotted `wb-control-error` */
  @Prop({ reflect: true, mutable: true })
  public error: boolean = false;

  /** Draws the checkbox in a checked state */
  @Prop({ mutable: true, reflect: true })
  public checked: boolean = false;

  /** Draws the checkbox in indeterminate state */
  @Prop({ mutable: true, reflect: true })
  public indeterminate: boolean = false;

  /** Determines whether the input is required or not */
  @Prop()
  public required?: boolean = false;

  /** The checkbox's value attribute */
  @Prop()
  public value: string = 'on';

  /** The checkbox's name attribute */
  @Prop()
  public name!: string;

  /** If content or message is not provided on expected slots, you should provide at least the aria-label to give more information about this field for accessibility reasons */
  @Prop()
  public inputAriaLabel: string;

  /** Emitted when the checked state changes */
  @Event()
  public wbchange: EventEmitter<{ name: string; value: string; checked: boolean }>;

  /** Emitted when the checked state changes */
  @Event({ composed: false })
  public change: EventEmitter<void>;

  /** Emitted when the checkbox loses focus */
  @Event()
  public wbblur: EventEmitter<void>;

  /** Emitted when the checkbox receives focus */
  @Event()
  public wbfocus: EventEmitter<void>;

  /** Returns a reference to the native input element */
  @Method()
  public async getInputReference(): Promise<HTMLInputElement> {
    return this.inputEl;
  }

  private inputEl!: HTMLInputElement;
  private randomInputId: string;
  private randomMessageId: string;

  private TagNames = transformTagNames(this, 'wb-checkbox', ['wb-icon', 'wb-text', 'wb-control-error', 'wb-control-hint']);

  public connectedCallback(): void {
    this.randomInputId = `input-${getRandomId()}`;
    this.randomMessageId = `${this.randomInputId}-message`;
    this.updateDirection();
  }

  public componentDidLoad(): void {
    if (this.indeterminate) {
      this.inputEl.indeterminate = true;
    }
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection(): void {
    applyAttribute(this.el, 'rtl', SharedDirectionService.isRtl);
  }

  private bindRef = (el) => {
    this.inputEl = el;
  };

  private handleMessageSlotChange = () => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.id = messageEl.id || this.randomMessageId;
      this.inputEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.inputEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private handleChange = (evt: InputEvent): void => {
    this.indeterminate = (evt.target as HTMLWbCheckboxElement).indeterminate;
    this.checked = (evt.target as HTMLWbCheckboxElement).checked;
    this.wbchange.emit(this.getValueChangeEventDetail());
    this.change.emit();
  };

  private handleBlur = () => this.wbblur.emit();

  private handleFocus = () => this.wbfocus.emit();

  private getValueChangeEventDetail(): { name: string; value: string; checked: boolean } {
    return { name: this.name, value: this.value, checked: this.checked };
  }

  private hasError(): string {
    return this.el.querySelector(`${this.TagNames.WbControlError}[slot="message"]`) != null ? 'true' : 'false';
  }

  public render(): JSX.Element {
    const { WbIcon, WbText } = this.TagNames;

    renderInputNativeInputChild(this.el, {
      type: 'checkbox',
      name: this.name,
      value: this.value,
      checked: this.checked,
      indeterminate: this.indeterminate,
      disabled: this.disabled,
    });

    return (
      <Host class="wb-checkbox">
        <label class="wrapper">
          <input //
            ref={this.bindRef}
            type="checkbox"
            id={this.randomInputId}
            checked={this.checked}
            disabled={this.disabled}
            value={this.value}
            name={this.name}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            required={this.required}
            indeterminate={this.indeterminate}
            aria-invalid={this.hasError()}
            aria-label={this.inputAriaLabel}
          />
          <div class="indicator">
            <WbIcon class="icon" name="bds/checkmark/16" />
          </div>
          <WbText size="l" static>
            <slot />
          </WbText>
        </label>
        <div class="message-container" aria-live="polite">
          <slot name="message" onSlotchange={this.handleMessageSlotChange} />
        </div>
      </Host>
    );
  }
}
